/* eslint-disable */
import { useCallback, useEffect, useState } from 'react';
import usePreloadAssets from './usePreloadAssets';
import useVTController from './useVTController';
import { isUserLoggedIn } from "shared/libraries/auth";
import {VirtualTourModel} from "shared/libraries/backend/VirtualTour";
import {ResultAssetResolution} from "shared/libraries/backend/ResultAsset";
import Player from "apps/home-viewer/360player";

export const MODES = {
  'WEB': 'WEB',
  'VIDEO_CAPTURE': 'VIDEO_CAPTURE',
};

export const SCREENS = {
  'LOADING': 'LOADING',
  'HOME_EXTERIOR': 'HOME_EXTERIOR',
  'HOME_2D': 'HOME_2D',
  'HOME_VIRTUAl_TOUR': 'HOME_VIRTUAl_TOUR',
};

export default function useAppController(logEventRef) {
  // Application state.
  const [state, setState] = useState({
    apiDataLoaded: false,
    backButtonDest: SCREENS.LANDING_PAGE,
    currentMode: MODES.WEB,
    currentScreen: SCREENS.LOADING,
    prevScreen: SCREENS.LOADING,
    selectedHome: null,
    isTooltipClicked: false,
    isPreviewing: false,
    vtCurrentCameraName: '',
    vtCurrentProducts: [],
    vtCurrentRoomName: '',
    vtCurrentZoneName: '',
    vtHasInteriorZones: false,
    vtSceneData: {},
    vtSelectedHotspot: null,
    MODES,
    SCREENS,
    error: null,
  });

  const [CONFIG_LOADED, setConfigLoaded] = useState(false);

  // Load the virtual tour json file on start-up.
  useEffect(
    () => {
      async function fetchConfigFile() {
        const selectedHome = { tour: { json: null }};
        const search = window.location.search;
        const params = new URLSearchParams(search);
        const virtualTourId = params.get('vtid');
        if (!virtualTourId) {
          setState(prevState => ({
            ...prevState,
            error: new Error('missing virtual tour id'),
          }));
          return;
        }

        let tour;
        try {
          if(!isUserLoggedIn()) {
            tour = await VirtualTourModel.publicView(virtualTourId);
          } else {
            tour = await VirtualTourModel.view(virtualTourId);
          }
          if (!(tour?.cameras?.length)) {
            setState(prevState => ({
              ...prevState,
              error: new Error(`virtual tour ${virtualTourId} is missing cameras`),
            }));
            return;
          }
          //FIXME this is adding the preview_url so that this can be used by the UI
          if (tour?.assets?.length) {
            tour.cameras.map(camera => {
              camera.preview_url = Player.getCubemapUrl(tour.assets, camera.name, ResultAssetResolution.K1);
            });
          }
        } catch(e) {
          setState(prevState => ({
            ...prevState,
            error: e,
          }));
          return;
        }
        setConfigLoaded(true);
        // Ignore rooms with no cameras.
        tour.zones = tour?.zones?.reduce((acc, zone) => {
          acc.push({
            ...zone,
            rooms: zone.rooms.filter(room => room.cameras.length > 0),
          });
          return acc;
        }, []) || [];
        selectedHome.tour.json = tour;

        setState(prevState => ({
          ...prevState,
          apiDataLoaded: !!(selectedHome?.tour?.json),
          currentScreen: SCREENS.HOME_VIRTUAl_TOUR,
          prevScreen: SCREENS.LOADING,
          selectedHome,
          isPreviewing: params.get("previewing") === "on",
          error: null,
        }));
      }

      if (!CONFIG_LOADED) {
        fetchConfigFile();
      }
    },
    []
  );

  // Initialize the virtual tour player on startup.
  const {
    moveToRoom,
    moveToZone,
  } = useVTController({
    selectedHome: state.selectedHome,
    vtSceneData: state.vtSceneData,
    setState,
    configLoaded: CONFIG_LOADED,
    logEventRef,
  });

  // Event handlers
  const dispatch = useCallback(
    async (event) => {
      console.log('event:', event);

      if (event.type === 'explore-home-button-clicked') {
        setState((prevState) => ({
          ...prevState,
          backButtonDest: SCREENS.LANDING_PAGE,
          currentScreen: prevState?.selectedHome?.backgroundVideo?.url ? SCREENS.HOME_EXTERIOR : SCREENS.HOME_2D,
          prevScreen: prevState.currentScreen,
        }));
        return;
      }

      if (event.type === 'explore-this-home-button-clicked') {
        setState((prevState) => ({
          ...prevState,
          tooltipFadeOut: true,
        }))
        setTimeout(
          () => {
            setState((prevState) => ({
              ...prevState,
              backButtonDest: SCREENS.COMMUNITY_OVERVIEW,
              currentScreen: prevState?.selectedHome?.backgroundVideo?.url ? SCREENS.HOME_EXTERIOR : SCREENS.HOME_2D,
              prevScreen: prevState.currentScreen,
              tooltipFadeOut: false,
            }));
          },
          200
        );
        return;
      }

      if (
        event.type === 'home-interior-button-clicked' ||
        event.type === 'enter-home-button-clicked'
      ) {
        setState((prevState) => ({
          ...prevState,
          currentScreen: SCREENS.HOME_VIRTUAl_TOUR,
          prevScreen: prevState.currentScreen,
        }));
        return;
      }

      if (event.type === 'home-two-d-button-clicked') {
        setState((prevState) => ({
          ...prevState,
          currentScreen: SCREENS.HOME_2D,
          prevScreen: prevState.currentScreen,
        }));
        return;
      }

      if (event.type === 'play-button-clicked') {
        setState((prevState) => ({
          ...prevState,
          currentScreen: (
            prevState.currentScreen === SCREENS.AMENITY_OVERVIEW ||
            prevState.currentScreen === SCREENS.AMENITY_2D
          ) ? SCREENS.AMENITY_OVERVIEW : SCREENS.HOME_EXTERIOR,
          prevScreen: prevState.currentScreen,
        }));
        return;
      }

      if (event.type === 'close-product-carousel-clicked') {
        setState((prevState) => ({
          ...prevState,
          vtSelectedHotspot: null,
        }));
        return;
      }

      if (event.type === 'zone-selector-zone-menu-item-clicked') {
        moveToZone(event?.payload?.zone?.name);
        return;
      }

      if (event.type === 'zone-selector-room-menu-item-clicked') {
        moveToRoom(event?.payload?.room?.name, true);
        return;
      }
    },
    [moveToRoom, moveToZone]
  );

  // Handle key press events
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code === 'KeyV') {
        setState(prevState => {
          const nextMode = prevState.currentMode === MODES.WEB ? MODES.VIDEO_CAPTURE : MODES.WEB;
          return {
            ...prevState,
            currentMode: nextMode,
          };
        });
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  // Preload important assets so the app feels quicker when moving between pages
  // usePreloadAssets({ state }); // We no liki this for now (2024/03/19)

  return [state, dispatch];
}
